<template>
  <v-dialog v-model="dialog" persistent max-width="500">
    <v-card>
      <v-card-text>
        <v-row>
          <!-- CAMERA SCANNER -->
          <v-col v-if="!physicalScanner" cols="12">
            <qrcode-stream
              v-if="dialog"
              :key="cameraFacing"
              :camera="cameraFacing"
              :paused="paused"
              @decode="decode"
              @init="onInit"
            >
              <template #loading>
                <v-progress-circular indeterminate color="primary" />
              </template>
              <template #error="err">
                <v-alert type="error" variant="tonal">
                  {{ err?.message || $t("Camera access error") }}
                </v-alert>
              </template>
            </qrcode-stream>
          </v-col>

          <!-- PHYSICAL SCANNER -->
          <v-col v-else cols="12">
            <v-row justify="center" class="mt-3 mb-3">
              <div
                v-if="!physicalScannerError && found === null"
                class="ma-3 text-blue text-subtitle-1"
              >
                {{ $t("parts.insert_parts.qr_scanner.physical_scanner") }}
              </div>
              <div
                v-else-if="physicalScannerError"
                class="ma-3 text-red text-subtitle-1"
              >
                {{ $t("parts.insert_parts.qr_scanner.physical_scanner_error") }}
              </div>
            </v-row>
          </v-col>

          <!-- LOADER -->
          <v-col cols="12" class="text-center">
            <v-progress-circular v-if="loading" indeterminate color="primary" />
          </v-col>

          <!-- ALERT -->
          <v-col cols="12">
            <v-alert
              v-if="alert.value"
              :type="alert.type"
              variant="tonal"
              border="bottom"
            >
              {{ alert.message }}
            </v-alert>
          </v-col>

          <!-- ACTION BUTTONS -->
          <v-col
            v-if="found && (mode === 'single' || mode === 'multiple')"
            cols="12"
            class="text-center"
          >
            <div class="qr-scanner-action-btns mb-2">
              <v-btn variant="outlined" @click="edit">
                {{ $t("parts.insert_parts.qr_scanner.edit_part") }}
                <v-icon end>mdi-pencil</v-icon>
              </v-btn>
              <v-btn variant="outlined" @click="actions">
                {{ $t("parts.table.actions") }}
                <v-icon end>mdi-compare-horizontal</v-icon>
              </v-btn>
            </div>
            <v-btn variant="outlined" block @click="scanAnotherPart">
              {{ $t("parts.insert_parts.qr_scanner.scan_another_part") }}
              <v-icon end>mdi-autorenew</v-icon>
            </v-btn>
          </v-col>

          <!-- SCANNED PARTS CHIPS -->
          <v-col
            v-if="mode === 'multiple' && idArray.length > 0"
            cols="12"
            class="mt-2 text-center"
          >
            <v-chip
              v-for="id in idArray"
              :key="id"
              class="ma-1"
              closable
              @click:close="removeId(id)"
            >
              {{ id.slice(-6) }}
            </v-chip>
          </v-col>

          <!-- MODE: LOCATION -->
          <v-col
            v-if="found && mode === 'location'"
            cols="12"
            class="text-center"
          >
            <v-btn
              variant="outlined"
              color="success"
              :loading="loadingLocation"
              @click="changeLocation"
            >
              {{ $t("parts.insert_parts.qr_scanner.change_location") }}
            </v-btn>
          </v-col>

          <!-- MODE: KIT -->
          <v-col
            v-if="found && mainPart && mode === 'kit'"
            cols="12"
            class="text-center"
          >
            <v-btn variant="outlined" color="success" @click="selectMainPart">
              {{ $t("parts.insert_parts.qr_scanner.select_main_part") }}
              <v-icon end>mdi-check</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-btn icon @click="reScan"><v-icon>mdi-refresh</v-icon></v-btn>
        <v-btn icon @click="switchScanner"><v-icon>mdi-qrcode</v-icon></v-btn>
        <v-btn icon @click="switchCamera"
          ><v-icon>mdi-camera-switch</v-icon></v-btn
        >
        <v-spacer />
        <v-btn variant="outlined" color="error" @click="close">
          {{ $t("parts.insert_parts.qr_scanner.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import getRequest from "../../api/getRequest";
import postRequest from "../../api/postRequest";
import { QrcodeStream } from "vue3-qrcode-reader";
export default {
  components: {
    QrcodeStream,
  },
  data: () => ({
    dialog: false,
    /** Prefer rear on mobile; 'auto' falls back to front/webcam on desktop (unlike 'rear'). */
    cameraFacing: "auto",
    paused: false,
    loading: false,
    loadingLocation: false,
    loadingBtn: false,
    alert: {
      value: false,
      type: "success",
      message: "",
    },
    part: {},
    found: null,
    decodedString: null,
    mainPart: false,
    physicalScanner: false,
    physicalScannerError: false,
    mode: null, // 'single' | 'multiple' | 'kit' | 'location'
    buffer: "",
    idArray: [],
    locationData: null,
  }),
  methods: {
    async onInit(promise) {
      try {
        await promise;
        this.alert.value = false;
      } catch (err) {
        if (err.name === "NotAllowedError") {
          this.alert = {
            value: true,
            type: "error",
            message: this.$t(
              "Camera access denied. Using physical scanner mode.",
            ),
          };
          this.switchScanner();
        } else if (
          (err.name === "OverconstrainedError" ||
            err.name === "NotFoundError") &&
          this.cameraFacing !== "auto"
        ) {
          // Desktop webcams have no rear camera; 'rear' uses an exact constraint.
          this.cameraFacing = "auto";
        } else {
          this.alert = {
            value: true,
            type: "error",
            message: this.$t("Cannot access camera."),
          };
        }
      }
    },
    switchCamera() {
      this.physicalScanner = false;
      document.removeEventListener("keydown", this.listenToEvent);
      this.cameraFacing = this.cameraFacing === "front" ? "rear" : "front";
    },
    switchScanner() {
      this.physicalScanner = true;
      document.addEventListener("keydown", this.listenToEvent);
    },
    listenToEvent(e) {
      if (e.key !== "Enter") this.buffer += e.key;
      else {
        if (this.buffer.length > 24) this.physicalScannerError = true;
        else this.decode(this.buffer);
        this.buffer = "";
      }
    },
    decode(value) {
      if (value !== null && value.length > 0) {
        this.loading = true;
        this.found = null;
        this.decodedString = value;
        this.paused = true;

        if (this.mode === "location") {
          // Scan location.
          getRequest("/repository/shelf/", `id=${value}`)
            .then((response) => {
              this.found = true;
              this.loading = false;
              const { repository } = response;
              const room = repository.rooms[response.roomIndex];
              const location = room.locations[response.locationIndex];
              const { shelf } = response;
              this.locationData = {
                repository,
                room,
                location,
                shelf,
              };
              this.alert.value = true;
              this.alert.type = "success";
              this.alert.message = `${this.$t(
                "parts.insert_parts.qr_scanner.found_shelf",
              )} ${this.decodedString}`;
            })
            .catch(() => {
              this.found = false;
              this.loading = false;
              this.alert.value = true;
              this.alert.type = "error";
              this.alert.message = `${this.$t(
                "parts.insert_parts.qr_scanner.cant_find_shelf",
              )} ${this.decodedString}`;
            });
        } else {
          // Scan part.
          getRequest(`/temporary-part/single/${value}/`)
            .then((response) => {
              if (response.temporaryPart && response.temporaryPart.length > 0) {
                [this.part] = response.temporaryPart;
                this.found = true;
                if (this.part.kit_main_part) {
                  this.mainPart = true;
                } else {
                  this.mainPart = false;
                }

                if (!this.idArray.includes(this.part._id)) {
                  this.idArray.push(this.part._id);
                }

                if (this.mode === "kit" && this.mainPart === false) {
                  this.alert.value = true;
                  this.alert.type = "error";
                  this.alert.message = `${this.$t(
                    "parts.insert_parts.qr_scanner.not_found_message",
                  )} ${this.decodedString}`;
                } else {
                  this.alert.value = true;
                  this.alert.type = "success";
                  this.alert.message = `${this.$t(
                    "parts.insert_parts.qr_scanner.found_message",
                  )} ${this.decodedString}`;
                }
              } else {
                this.found = false;
                this.alert.value = true;
                this.alert.type = "error";
                this.alert.message = `${this.$t(
                  "parts.insert_parts.qr_scanner.not_found_message",
                )} ${this.decodedString}`;
              }
              this.loading = false;
            })
            .catch(() => {
              this.found = false;
              this.loading = false;
            });
        }
      }
    },
    reScan() {
      this.decodedString = null;
      this.found = null;
      if (this.mode === "kit" && this.found === null) {
        this.alert.value = true;
        this.alert.type = "info";
        this.alert.message = this.$t(
          "parts.insert_parts.qr_scanner.scan_main_part_message",
        );
      } else if (this.mode === "location") {
        this.alert = {
          value: true,
          type: "info",
          message: this.$t(
            "parts.insert_parts.qr_scanner.scan_another_part_message",
          ),
        };
      } else {
        this.alert = {
          value: false,
          type: "success",
          message: "",
        };
      }
      this.physicalScannerError = false;
      this.loading = false;
      this.paused = false;
    },
    open(data) {
      this.cameraFacing = "auto";
      this.dialog = true;
      this.mode = data.mode;
      if (this.mode === "kit" && this.found === null) {
        this.alert.value = true;
        this.alert.type = "info";
        this.alert.message = this.$t(
          "parts.insert_parts.qr_scanner.scan_main_part_message",
        );
      }
    },
    close() {
      this.dialog = false;
      this.paused = false;
      this.loading = false;
      this.part = {};
      this.found = null;
      this.alert = {
        value: false,
        type: "success",
        message: "",
      };
      this.decodedString = null;
      this.mainPart = false;
      this.physicalScanner = false;
      this.physicalScannerError = false;
      document.removeEventListener("keydown", this.listenToEvent);
      this.$emit("close");
      this.cameraFacing = "auto";
    },
    async edit() {
      const partSnapshot = this.part;
      this.close();
      await this.$nextTick();
      this.$emit("edit", partSnapshot, false);
    },
    async actions() {
      const partSnapshot = this.part;
      this.close();
      await this.$nextTick();
      this.$emit("actions", partSnapshot);
    },
    create() {
      this.loadingBtn = true;
      postRequest
        .object(`/temporary-part/insert/empty/single/${this.decodedString}`, {
          user_id: this.$store.getters.userId,
          institution_id: this.$store.getters.institutionId,
        })
        .then((response) => {
          this.loadingBtn = false;
          this.part = response.data.partResponse;
          this.edit();
        })
        .catch((error) => {
          this.loadingBtn = false;
          if (error.response.status === 403) {
            this.alert.value = true;
            this.alert.type = "error";
            this.alert.message = `${this.$t(
              "parts.insert_parts.qr_scanner.part_exists_message_1",
            )} ${this.decodedString} ${this.$t(
              "parts.insert_parts.qr_scanner.part_exists_message_2",
            )}`;
          } else {
            this.alert.value = true;
            this.alert.type = "error";
            this.alert.message = this.$t(
              "parts.insert_parts.qr_scanner.create_error_message",
            );
          }
        });
    },

    scanAnotherPart() {
      this.mode = "multiple";
      this.decodedString = null;
      this.found = null;
      this.physicalScannerError = false;
      this.loading = false;
      this.paused = false;
      this.alert = {
        value: true,
        type: "info",
        message: this.$t(
          "parts.insert_parts.qr_scanner.scan_another_part_message",
        ),
      };
    },
    removeId(id) {
      const index = this.idArray.indexOf(id);
      if (index > -1) this.idArray.splice(index, 1);
    },
    switchToLocationChanger() {
      this.mode = "location";
      this.decodedString = null;
      this.found = null;
      this.physicalScannerError = false;
      this.loading = false;
      this.paused = false;
      this.alert = {
        value: true,
        type: "info",
        message: this.$t("parts.insert_parts.qr_scanner.scan_shelf_message"),
      };
    },
    changeLocation() {
      this.loadingLocation = true;
      postRequest
        .object("/temporary-part/insert/location", {
          parts_ids: this.idArray,
          repository_id: this.locationData.repository._id,
          room_id: this.locationData.room._id,
          location_id: this.locationData.location._id,
          shelf_id: this.locationData.shelf._id,
        })
        .then(() => {
          this.loadingLocation = false;
          this.$emit("changed-location");
          this.close();
        })
        .catch((error) => {
          this.loadingLocation = false;
          this.alert = {
            value: true,
            type: "error",
            message: error,
          };
        });
    },
    /* KIT -------------------------------------------------------------------------------------- */
    selectMainPart() {
      this.$emit("select-main-part", this.part.kit_id);
      this.close();
    },
  },
};
</script>

<style scoped>
.qr-scanner-action-btns {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qr-scanner-action-btns .v-btn {
  width: 100%;
}

@media (min-width: 960px) {
  .qr-scanner-action-btns {
    flex-direction: row;
  }

  .qr-scanner-action-btns .v-btn {
    flex: 1 1 0;
    min-width: 0;
    width: auto;
  }
}
</style>
